import { MaxLeadsPerWeekAndPeriodForm } from "@/components/campaign/setup/planning/MaxLeadsPerWeekAndPeriodForm";
import { SchedulesAndPeriodForm } from "@/components/campaign/setup/planning/SchedulesAndPeriodForm";
import { entriesToRecord } from "@/utils/array-utils";
import { WEEKDAYS } from "@/utils/dates";
import { useStepperSubmitHandlerState } from "@/utils/stepper";
import { getLocalTimeZone, parseDate, today } from "@internationalized/date";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import { DateValue } from "react-aria";

export const Route = createFileRoute(
  "/_dashboard/campaigns/setup/planning/lead-amount-and-period",
)({
  component: RouteComponent,
  staticData: {
    meta: {
      title: "Antal leads & periode",
    },
  },
});

const getPeriodValues = ({
  chosenStartDate,
  chosenEndDate,
}: {
  chosenStartDate: DateValue | null;
  chosenEndDate: DateValue | null;
}) => ({
  startDate: (chosenStartDate ?? today(getLocalTimeZone()))?.toString(),
  endDate: chosenEndDate?.toString() ?? null,
});

function RouteComponent() {
  const context = Route.useRouteContext();
  const [newCampaignState] = context.useNewCampaignState();

  switch (newCampaignState.planning?.type) {
    case "Scheduled":
      return <ScheduledRouteComponent />;
    case "Triggered":
      return <TriggeredRouteComponent />;
    default:
      throw Error("Campaign type not supported by this route");
  }
}

function ScheduledRouteComponent() {
  const context = Route.useRouteContext();

  const stepper = context.useStepper();

  const planningContext = useMemo(
    () =>
      stepper.context.planning?.type === "Scheduled"
        ? stepper.context.planning
        : null,
    [stepper.context],
  );

  if (!planningContext) throw null;

  const values = useMemo(() => {
    const amountsByDayOfWeek = new Map(
      planningContext.schedules.map((schedule) => [
        schedule.dayOfWeek.toLowerCase(),
        schedule.amount,
      ]),
    );

    return {
      schedules: entriesToRecord(
        WEEKDAYS.map((day) => [day.id, amountsByDayOfWeek.get(day.id) ?? null]),
      ),
      period: getPeriodValues(planningContext),
    };
  }, [planningContext]);

  const [submitHandler, setSubmitHandler] = useStepperSubmitHandlerState();

  context.stepper.useStepperSubmit(submitHandler);

  return (
    <SchedulesAndPeriodForm
      defaultValues={values}
      onSubmit={useCallback(
        (data) => {
          stepper.setContext((prev) => {
            if (prev.planning?.type !== "Scheduled") return prev;

            return {
              ...prev,
              planning: {
                ...prev.planning,
                schedules: Object.entries(data.schedules)
                  .map(([day, amount]) =>
                    amount !== null
                      ? {
                          dayOfWeek: day,
                          amount: amount,
                        }
                      : null,
                  )
                  .filter((schedule) => schedule !== null),
                chosenStartDate: parseDate(data.period.startDate),
                chosenEndDate: data.period.endDate
                  ? parseDate(data.period.endDate)
                  : null,
              },
            };
          });

          stepper.goNext();
        },
        [stepper],
      )}
      setSubmitHandler={setSubmitHandler}
    />
  );
}

function TriggeredRouteComponent() {
  const context = Route.useRouteContext();

  const stepper = context.useStepper();

  const planningContext = useMemo(
    () =>
      stepper.context.planning?.type === "Triggered"
        ? stepper.context.planning
        : null,
    [stepper.context],
  );

  if (!planningContext) throw null;

  const values = useMemo(() => {
    return {
      maxLeadsPerWeek: planningContext.maxLeadsPerWeek,
      period: getPeriodValues(planningContext),
    };
  }, [planningContext]);

  const [submitHandler, setSubmitHandler] = useStepperSubmitHandlerState();

  context.stepper.useStepperSubmit(submitHandler);

  return (
    <MaxLeadsPerWeekAndPeriodForm
      defaultValues={values}
      onSubmit={useCallback(
        (data) => {
          stepper.setContext((prev) => {
            if (prev.planning?.type !== "Triggered") return prev;

            return {
              ...prev,
              planning: {
                ...prev.planning,
                maxLeadsPerWeek: data.maxLeadsPerWeek,
                chosenStartDate: parseDate(data.period.startDate),
                chosenEndDate: data.period.endDate
                  ? parseDate(data.period.endDate)
                  : null,
              },
            };
          });

          stepper.goNext();
        },
        [stepper],
      )}
      setSubmitHandler={setSubmitHandler}
    />
  );
}
